import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import type { PresignResult } from '@/lib/upload';

import { toListQuery } from './filters';
import {
  orderAssigneeOptionSchema,
  orderCustomerOptionSchema,
  orderDetailSchema,
  orderListResponseSchema,
  orderReviewSchema,
  parameterTemplateOptionSchema,
} from './schemas';
import type {
  AddParametersInput,
  OrderAssigneeOption,
  OrderCustomerOption,
  OrderDetail,
  OrderListParams,
  OrderListResponse,
  OrderReview,
  OrderStatus,
  ParameterTemplateOption,
  RaiseTicketInput,
  RequestReplacementInput,
} from './types';

export { toListQuery };

/** Paginated/sorted/filtered/searchable order list. `payment_failed` rows are excluded server-side. */
export async function listOrders(params: OrderListParams): Promise<OrderListResponse> {
  const { page, limit, sortBy = 'orderedAt', sortOrder = 'desc', ...filters } = params;
  const { data, meta } = await apiClient.getList(endpoints.orders.list, {
    params: { page, limit, sortBy, sortOrder, ...toListQuery(filters) },
  });
  return orderListResponseSchema.parse({ data, meta });
}

/** Distinct cities present on orders (City filter options). Loaded once. */
export async function listOrderCities(): Promise<string[]> {
  const data = await apiClient.get<string[]>(endpoints.orders.metaCities);
  return data.map(String);
}

/** Server-searched customer typeahead (capped backend-side). */
export async function listOrderCustomers(search: string): Promise<OrderCustomerOption[]> {
  const data = await apiClient.get(endpoints.orders.metaCustomers, { params: { search } });
  return orderCustomerOptionSchema.array().parse(data);
}

// ── Order Detail (feature 102116) ─────────────────────────────────────────────

/** Full order detail. */
export async function getOrder(id: string): Promise<OrderDetail> {
  const data = await apiClient.get(endpoints.orders.detail(id));
  return orderDetailSchema.parse(data);
}

/** Change order status (validated against STATUS_TRANSITIONS server-side). Returns the
 *  updated order. A `delivered` change while an installation request is pending is rejected. */
export async function updateOrderStatus(id: string, status: OrderStatus): Promise<OrderDetail> {
  const data = await apiClient.patch(endpoints.orders.status(id), { status });
  return orderDetailSchema.parse(data);
}

/** Set/edit the expected delivery date (must be ≥ order date). Auto-saved. */
export async function updateExpectedDelivery(
  id: string,
  expectedDelivery: string,
): Promise<OrderDetail> {
  const data = await apiClient.patch(endpoints.orders.expectedDelivery(id), { expectedDelivery });
  return orderDetailSchema.parse(data);
}

/** Cancel the order and initiate a refund; cancels any linked installation request. */
export async function cancelOrder(id: string): Promise<OrderDetail> {
  const data = await apiClient.post(endpoints.orders.cancel(id));
  return orderDetailSchema.parse(data);
}

/** Presigned invoice URL (ADR-0002). The SPA triggers a browser download. */
export async function getInvoiceUrl(id: string): Promise<string> {
  const data = await apiClient.get<{ url: string }>(endpoints.orders.invoice(id));
  return data.url;
}

/** Per-device customer reviews (Reviews drawer). */
export async function getOrderReviews(id: string): Promise<OrderReview[]> {
  const data = await apiClient.get(endpoints.orders.reviews(id));
  return orderReviewSchema.array().parse(data);
}

/** Raise a support ticket against the order (ticketType `order`, orderId attached). */
export async function raiseOrderTicket(id: string, input: RaiseTicketInput): Promise<void> {
  await apiClient.post(endpoints.orders.tickets(id), {
    ...input,
    orderId: id,
    ticketType: 'order',
  });
}

/** Raise a replacement request for one line item (delivered + within window). */
export async function requestReplacement(
  id: string,
  input: RequestReplacementInput,
): Promise<void> {
  await apiClient.post(endpoints.orders.replacements(id), input);
}

/** Save device parameter-template configuration; marks devices configured. */
export async function addOrderParameters(
  id: string,
  input: AddParametersInput,
): Promise<OrderDetail> {
  const data = await apiClient.post(endpoints.orders.parameters(id), input);
  return orderDetailSchema.parse(data);
}

/** Assignee options for the Raise-a-ticket form. */
export async function listOrderAssignees(): Promise<OrderAssigneeOption[]> {
  const data = await apiClient.get(endpoints.orders.metaAssignees);
  return orderAssigneeOptionSchema.array().parse(data);
}

/** Parameter-template options for the Add Parameters modal. */
export async function listParameterTemplates(): Promise<ParameterTemplateOption[]> {
  const data = await apiClient.get(endpoints.orders.metaParameterTemplates);
  return parameterTemplateOptionSchema.array().parse(data);
}

/** Presign → direct-PUT flow for replacement-request images (same storage contract as products). */
export async function getOrderPresignedUrl(file: File, folder: string): Promise<PresignResult> {
  const { url, fields } = await apiClient.post<{ url: string; fields: { key: string } }>(
    endpoints.storage.presignedUrl,
    { fileName: file.name, fileType: file.type, folder },
  );
  return {
    uploadUrl: url,
    file: {
      id: fields.key,
      url: fields.key,
      name: file.name,
      size: file.size,
      contentType: file.type,
    },
  };
}
